import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useEditor, EditorContent, Editor, BubbleMenu } from '@tiptap/react';
import Link from '@tiptap/extension-link';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Typography from '@tiptap/extension-typography';
import Underline from '@tiptap/extension-underline';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import Placeholder from '@tiptap/extension-placeholder';
import { common, createLowlight } from 'lowlight';
import { FileDown, Plus, X, Type, Grid2x2, RowsIcon, ColumnsIcon, Merge, Split, Send } from 'lucide-react';
import { Markdown } from 'tiptap-markdown';
import { Toolbar } from './editor/Toolbar';
import { BlockSelector } from './editor/BlockSelector';
import { BlockSelector as HtmlBlockSelector } from './editor/BlockHtmlSelector';
import { Extension, Node } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import ListItem from '@tiptap/extension-list-item';
import Dropcursor from '@tiptap/extension-dropcursor';
import Document from '@tiptap/extension-document';
import StarterKit from '@tiptap/starter-kit';
import { useAutoSave } from '../hooks/useAutoSave';
import { autoSaveService } from '../services/autoSaveService';

import { useIdleDetection } from '../hooks/useIdleDetection';
import DOMPurify from 'dompurify';
import { InteractiveKanbanNode } from './TiptapExtensions/InteractiveKanbanNode';
import { useDatabase } from '../contexts/DatabaseContext';
import { EditableTitle } from './EditableTitle';
// Импортируем новый компонент LinkPreview
import LinkPreview from './LinkPreview';
import InternalLinkPreview from './InternalLinkPreview';

// Импорт стилей из внешних файлов
import './editor/styles/editorStyles.css';
import './editor/styles/blockStyles.css';

// Инициализация lowlight для подсветки синтаксиса
const lowlightPlugin = createLowlight(common);

// Интерфейс для компонента меню блока
interface BlockMenuProps {
  isOpen: boolean;
  onClose: () => void;
  position: { x: number; y: number };
  editor: Editor;
}

// Модальное окно для выбора блока
const BlockMenu = ({ isOpen, onClose, position, editor }: BlockMenuProps) => {
  if (!isOpen) return null;

  const handleAddBlock = (type: string) => {
    switch (type) {
      case 'image':
        const url = prompt('Введите URL изображения:');
        if (url) {
          editor.chain().focus().setImage({ src: url }).run();
        }
        break;
      case 'kanban':
        try {
          console.log('Вставляем канбан-доску из меню блоков');
          // Генерируем уникальный ID здесь
          const newKanbanId = `kanban-${crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(16).slice(2)}`;
          const kanbanAttrs = { id: newKanbanId };

          // Проверяем, может ли редактор выполнить команду вставки
          if (editor.can().chain().focus().insertContent({ type: 'kanbanBoard', attrs: kanbanAttrs }).run()) {
            // Вставляем канбан-доску с сгенерированным ID
            editor.chain().focus().insertContent({ type: 'kanbanBoard', attrs: kanbanAttrs }).run();
            console.log('Канбан-доска успешно вставлена из меню блоков с ID:', newKanbanId);
          } else {
            console.error('Редактор не может вставить канбан-доску в текущем контексте');
          }
        } catch (error) {
          console.error('Ошибка при вставке канбан-доски из меню блоков:', error);
        }
        break;
      default:
        break;
    }
    onClose();
  };

  return (
    <div 
      className="block-menu"
      style={{
        position: 'absolute',
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 50,
      }}
    >
      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
        <div className="p-2">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 px-2 py-1">
            Блоки
          </h3>
          
          <div className="grid grid-cols-1 gap-1 mt-1">
            <button 
              className="flex items-center text-left rounded px-2 py-1 hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => handleAddBlock('kanban')}
            >
              <span className="text-lg">📋</span>
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Канбан-доска</span>
            </button>
          </div>
        </div>
      </div>
      
      <div 
        className="fixed inset-0 z-40" 
        onClick={onClose}
      />
    </div>
  );
};

// Создаем расширение для работы с новой строкой и меню блока
const NewLineHandling = Extension.create({
  name: 'newLineHandling',
  
  addKeyboardShortcuts() {
    return {
      Enter: () => {
        const { selection } = this.editor.state;
        const { $anchor, empty } = selection;
        
        // Если курсор находится в пустом параграфе, создаем новый пустой
        if (empty && $anchor.parent.type.name === 'paragraph' && $anchor.parent.textContent === '') {
          return this.editor.chain().focus().createParagraphNear().run();
        }
        
        // Если курсор находится в конце параграфа (не пустого), также создаем новый параграф
        if ($anchor.pos === $anchor.end() && !empty) {
          return this.editor.chain().focus().createParagraphNear().run();
        }
        
        // Иначе просто новая строка
        return false;
      }
    };
  },
  
  // Добавляем обработку автоматического создания новой строки при достижении конца документа
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('clickBelowBlockHandler'), // Дадим ключу имя для отладки
        props: {
          handleClick: (view, pos, event) => {
            const { state, dispatch } = view;
            const editor = this.editor; // Получаем доступ к экземпляру Tiptap editor
            const mouseEvent = event as MouseEvent;
            const editorDOM = view.dom as HTMLElement;

            // 1. Получаем позицию в документе по координатам клика
            const clickCoords = { left: mouseEvent.clientX, top: mouseEvent.clientY };
            const posInfo = view.posAtCoords(clickCoords);

            if (!posInfo) {
              // Клик вне области редактора
              return false;
            }

            const clickPos = posInfo.pos;
            const $clickPos = state.doc.resolve(clickPos);

            // 2. Проверяем, не был ли клик ВНУТРИ существующего контента
            const parentNode = $clickPos.parent;
            const isAtEndOfTextNode = parentNode.isTextblock && $clickPos.parentOffset === parentNode.content.size;

            // Если клик внутри узла (posInfo.inside > -1) ИЛИ
            // если клик внутри текстового блока, но не в самом конце его содержимого
            if (posInfo.inside > -1 || (parentNode.isTextblock && !isAtEndOfTextNode)) {
                 console.log("Клик внутри контента или не в конце текстового блока, пропускаем.", { posInfo, $clickPos });
                 return false; // Пусть ProseMirror обработает стандартно (переместит курсор)
            }

            // 3. Ищем ближайший БЛОЧНЫЙ узел НАД позицией клика
            let targetNodePos: number | null = null;
            let targetNode: any = null; // Используем any для совместимости с кодом пользователя

            for (let d = $clickPos.depth; d >= 0; d--) {
              const node = $clickPos.node(d);
              // Ищем блочные узлы
              if (node.isBlock) {
                try {
                  // Пропускаем сам документ (depth 0)
                  if (d === 0) continue;

                  const nodePos = $clickPos.before(d); // Позиция НАЧАЛА этого узла
                  const nodeDOM = view.nodeDOM(nodePos) as HTMLElement;

                  if (nodeDOM) {
                    const rect = nodeDOM.getBoundingClientRect();
                    const distance = mouseEvent.clientY - rect.bottom;
                    const isInHorizontalBounds = mouseEvent.clientX >= rect.left && mouseEvent.clientX <= rect.right;

                    // console.log("Проверка блока:", { nodeType: node.type.name, nodePos, rect, clickY: mouseEvent.clientY, distance, isInHorizontalBounds, depth: d });

                    // Если клик ниже этого блока и в его горизонтальных границах и достаточно близко
                    if (distance > 0 && distance < 30 && isInHorizontalBounds) {
                        targetNodePos = nodePos;
                        targetNode = node;
                        console.log("Найден целевой блок для вставки ПОСЛЕ:", targetNode.type?.name || "неизвестный тип");
                        break; // Нашли самый вложенный подходящий блок
                    }
                  } else {
                     // console.warn("Не удалось получить nodeDOM для узла", { nodeType: node.type.name, nodePos });
                  }
                } catch (error) {
                  console.error("Ошибка при обработке узла в поиске блока:", error, { nodeType: node.type.name, depth: d });
                  continue; // Пропускаем этот узел при ошибке
                }
              }
            }

            // 4. Если нашли блок, под которым кликнули
            if (targetNode && targetNodePos !== null) {
              try {
                const posAfter = targetNodePos + (targetNode.nodeSize || 0);

                // Проверяем, можно ли вставить параграф здесь
                const $posAfter = state.doc.resolve(posAfter);
                const canInsert = $posAfter.parent.canReplaceWith($posAfter.index(), $posAfter.index(), state.schema.nodes.paragraph);

                if (canInsert) {
                   // ---> НАЧАЛО ИЗМЕНЕНИЯ 1 <---
                   // Проверяем, является ли блок, ПОСЛЕ которого мы хотим вставить, пустым параграфом
                   if (targetNode.type === state.schema.nodes.paragraph && targetNode.content.size === 0) {
                     console.log("Пропускаем вставку: предыдущий блок - пустой параграф.");
                     // Опционально: перенести фокус в этот пустой параграф
                     editor.chain().focus($posAfter.pos - 1).run();
                     return true; // Обработали клик (переместили фокус), но не вставили новый блок
                   }
                   // ---> КОНЕЦ ИЗМЕНЕНИЯ 1 <---

                   console.log("Вставляем параграф ПОСЛЕ блока:", {
                     nodeType: targetNode.type?.name || "неизвестный тип",
                     posAfter
                   });

                   // Используем команды Tiptap
                   editor.chain()
                     .insertContentAt(posAfter, { type: 'paragraph' }) // Вставляем как объект Tiptap
                     .focus(posAfter + 1) // Фокус внутри нового параграфа
                     .run();
                   return true; // Сообщаем, что обработали клик
                } else {
                   console.log("Нельзя вставить параграф после блока:", {
                     nodeType: targetNode.type?.name || "неизвестный тип",
                     posAfter,
                     parentType: $posAfter.parent.type.name,
                     index: $posAfter.index()
                   });
                }
              } catch (error) {
                console.error("Ошибка при вставке параграфа после блока:", error);
                // Если произошла ошибка при вставке, продолжаем выполнение функции
              }
            }

            // 5. Если не нашли конкретный блок ИЛИ вставка после него невозможна,
            //    проверяем клик НИЖЕ ВСЕГО контента

            // Находим самый нижний *корневой* блочный элемент
            let lastBlockBottom = 0;
            const editorRect = editorDOM.getBoundingClientRect();
            // Ищем прямых потомков редактора, которые являются блоками
            const directChildBlocks = Array.from(editorDOM.children).filter(child => {
                // Проверяем стили или атрибуты, указывающие на блочное отображение или тип узла
                const style = window.getComputedStyle(child);
                return style.display === 'block' || child.matches('p, h1, h2, h3, div[data-type], ul, ol, blockquote, table, .interactive-kanban');
            });


            if (directChildBlocks.length > 0) {
              directChildBlocks.forEach(elem => {
                const rect = elem.getBoundingClientRect();
                if (rect.bottom > lastBlockBottom) {
                  lastBlockBottom = rect.bottom;
                }
              });

              // Если клик ниже последнего блока (с небольшим запасом)
              // И также позиция клика близка к концу документа
              // (Используем state.doc.content.size - 2, т.к. документ всегда заканчивается на `doc` и `paragraph` или др. блок)
              if (mouseEvent.clientY > lastBlockBottom + 5 && clickPos >= state.doc.content.size - 2) {

                // ---> НАЧАЛО ИЗМЕНЕНИЯ 2 <---
                // Проверяем, является ли последний узел в документе пустым параграфом
                const lastNode = state.doc.lastChild;
                if (lastNode && lastNode.type === state.schema.nodes.paragraph && lastNode.content.size === 0) {
                  console.log("Пропускаем вставку в конец: последний блок - пустой параграф.");
                  // Перемещаем фокус в этот последний пустой параграф
                  editor.chain().focus(state.doc.content.size - 1).run(); // Фокус на конец содержимого параграфа
                  return true; // Обработали клик (переместили фокус), но не вставили новый блок
                }
                // ---> КОНЕЦ ИЗМЕНЕНИЯ 2 <---

                console.log("Клик ниже последнего корневого блока. Вставляем параграф в конец документа.");

                 const endPos = state.doc.content.size;
                 editor.chain()
                    .insertContentAt(endPos, { type: 'paragraph' })
                    .focus(endPos + 1)
                    .run();
                 return true;
              } else {
                //  console.log("Клик не был определен как клик под конкретным блоком или ниже всего контента.", { clickY: mouseEvent.clientY, lastBlockBottom, clickPos, docSize: state.doc.content.size });
              }

            } else if (state.doc.content.size <= 2){ // Проверка на практически пустой документ (только <p></p> внутри doc)
               // Если вообще нет корневых блоков (или только пустой параграф), а клик был
               console.log("Редактор пуст или почти пуст. Вставляем параграф.");
               const insertPos = state.doc.content.size; // Вставить в самый конец
               editor.chain()
                 .insertContentAt(insertPos, { type: 'paragraph' })
                 .focus(insertPos + 1)
                 .run();
               return true;
            }


            // 6. Если ни одно из условий не выполнено
            return false; // Позволяем ProseMirror обработать клик по умолчанию
          }
        }
      })
    ];
  }
});

// Добавляем новое расширение для обработки CSS классов в HTML
const AllowClassesOnNodes = Extension.create({
  name: 'allowClassesOnNodes',
  
  addGlobalAttributes() {
    return [
      {
        types: [
          'paragraph',
          'heading',
          'blockquote',
          'bulletList',
          'orderedList',
          'listItem',
          'codeBlock',
          'horizontalRule',
          'image',
        ],
        attributes: {
          class: {
            default: null,
          },
          style: {
            default: null,
          },
          id: {
            default: null,
          },
        },
      },
      {
        types: ['div', 'span'],
        attributes: {
          class: {
            default: null,
          },
          style: {
            default: null,
          },
          id: {
            default: null,
          },
        },
      },
    ];
  },
});

// Создаем расширение для лучшей поддержки HTML
const HtmlSupport = Extension.create({
  name: 'htmlSupport',

  addGlobalAttributes() {
    return [
      {
        // Применяем только к блочным элементам, исключаем text
        types: [
          'paragraph', 
          'heading',
          // Убираем 'text', так как он не должен иметь атрибуты
          'bulletList',
          'orderedList', 
          'listItem',
          'blockquote',
          'codeBlock',
          'horizontalRule',
          'image',
          'taskList',
          'taskItem',
          'table',
          'tableRow',
          'tableCell',
          'tableHeader'
        ],
        attributes: {
          // Поддерживаем классы и стили
          class: {},
          style: {},
          id: {},
        }
      }
    ];
  },
});

// Заменяем BlockContainer на HTMLBlockNode
const HTMLBlockNode = Node.create({
  name: 'htmlBlock',
  group: 'block',
  // Удаляем atom: true, чтобы обеспечить редактирование содержимого
  content: 'block+', // Разрешаем внутренние блоки для редактирования
  draggable: true,
  
  addAttributes() {
    return {
      class: { default: null },
      style: { default: null },
      id: { default: null },
      'data-type': { default: 'html-block' }
    };
  },
  
  parseHTML() {
    return [{ tag: 'div[data-type="html-block"]' }];
  },
  
  renderHTML({ node, HTMLAttributes }) {
    // Возвращаем div с нашим типом и сохраняем атрибуты
    return ['div', { 
      'data-type': 'html-block', 
      ...HTMLAttributes 
    }, 0]; // Добавляем 0 для обозначения контента
  },
  
  addNodeView() {
    return ({ node, editor, getPos, HTMLAttributes, extension, decorations }) => {
      // Создаем контейнер
      const dom = document.createElement('div');
      dom.setAttribute('data-type', 'html-block');
      dom.classList.add('tiptap-nodeview-block');
      
      // Создаем contentDOM для редактируемого содержимого
      const contentDOM = document.createElement('div');
      contentDOM.classList.add('tiptap-nodeview-content');
      dom.appendChild(contentDOM);
      
      // Добавляем элемент для удаления блока
      const deleteButton = document.createElement('button');
      deleteButton.classList.add('tiptap-nodeview-delete');
      deleteButton.innerHTML = '✕';
      deleteButton.title = 'Удалить блок';
      dom.appendChild(deleteButton);
      
      // Добавляем остальные атрибуты из узла
      if (node.attrs.class) dom.classList.add(...node.attrs.class.split(/\s+/));
      if (node.attrs.style) dom.setAttribute('style', node.attrs.style);
      if (node.attrs.id) dom.id = node.attrs.id;
      
      // Обработчик удаления блока
      const handleDelete = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        
        if (typeof getPos === 'function') {
          const pos = getPos();
          editor.chain().focus().deleteRange({ from: pos, to: pos + node.nodeSize }).run();
        }
      };
      deleteButton.addEventListener('click', handleDelete);
      
      // Добавляем обработчик клика для выделения узла
      const handleClick = (event: MouseEvent) => {
        if (typeof getPos === 'function' && (event.target === dom || event.target === deleteButton)) {
          const pos = getPos();
          editor.commands.setNodeSelection(pos);
          dom.classList.add('tiptap-nodeview-selected');
        }
      };
      
      // Обработчик для предотвращения установки курсора вне contentDOM
      const handleMouseDown = (event: MouseEvent) => {
        // Если клик происходит на основном контейнере, но не внутри contentDOM,
        // предотвращаем действие по умолчанию, чтобы не создавался новый блок
        const target = event.target as Element;
        const isClickInContent = contentDOM.contains(target) || target === contentDOM;
        const isClickOnDelete = deleteButton.contains(target) || target === deleteButton;
        
        if (!isClickInContent && !isClickOnDelete && target === dom) {
          event.preventDefault();
          // Выделяем весь блок
          if (typeof getPos === 'function') {
            const pos = getPos();
            editor.commands.setNodeSelection(pos);
          }
        }
      };
      
      // Добавляем обработчик перетаскивания (для лучшего UX)
      dom.draggable = true;
      dom.addEventListener('dragstart', (event: DragEvent) => {
        if (typeof getPos === 'function') {
          const pos = getPos();
          editor.commands.setNodeSelection(pos);
        }
      });
      
      // Добавляем обработчики событий
      dom.addEventListener('click', handleClick);
      dom.addEventListener('mousedown', handleMouseDown);
      
      return {
        dom,
        contentDOM, // Устанавливаем contentDOM для редактирования содержимого
        update(updatedNode) {
          if (updatedNode.type.name !== node.type.name) return false;
          
          // Обновляем атрибуты
          if (updatedNode.attrs.class !== node.attrs.class) {
            dom.className = 'tiptap-nodeview-block';
            contentDOM.className = 'tiptap-nodeview-content';
            if (updatedNode.attrs.class) dom.classList.add(...updatedNode.attrs.class.split(/\s+/));
          }
          
          if (updatedNode.attrs.style !== node.attrs.style) {
            if (updatedNode.attrs.style) dom.setAttribute('style', updatedNode.attrs.style);
            else dom.removeAttribute('style');
          }
          
          if (updatedNode.attrs.id !== node.attrs.id) {
            if (updatedNode.attrs.id) dom.id = updatedNode.attrs.id;
            else dom.removeAttribute('id');
          }
          
          return true;
        },
        // Очистка ресурсов при удалении NodeView
        destroy() {
          dom.removeEventListener('click', handleClick);
          dom.removeEventListener('mousedown', handleMouseDown);
          deleteButton.removeEventListener('click', handleDelete);
        }
      };
    };
  },
});

// Создаем расширение для обработки вставки HTML
const EnhancedPasteHandler = Extension.create({
  name: 'enhancedPasteHandler',
  
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('enhancedPasteHandler'),
        props: {
          // Обработка вставки div-блоков из буфера обмена
          transformPasted: (slice) => {
            // Преобразование содержимого для сохранения div-блоков
            // Эта функция будет вызвана при вставке контента
            return slice;
          },
          
          handlePaste: (view, event, slice) => {
            // Обрабатываем вставку HTML-контента из буфера обмена
            if (event.clipboardData && event.clipboardData.getData('text/html')) {
              const html = event.clipboardData.getData('text/html');
              
              // Предварительная обработка HTML для сохранения div-блоков
              const processedHtml = html
                .replace(/<div([^>]*)>/g, '<div$1 data-type="block">')
                .replace(/\n\s+/g, ' ')
                .replace(/\s{2,}/g, ' ')
                .trim();
              
              try {
                // Вставляем через команду editor
                setTimeout(() => {
                  this.editor.commands.insertContent(processedHtml);
                }, 0);
                
                return true;
              } catch (error) {
                console.error('Ошибка при обработке вставки HTML:', error);
                // Если не удалось, продолжаем стандартную обработку
                return false;
              }
            }
            
            return false;
          }
        }
      })
    ];
  }
});

// Функция для безопасной вставки HTML-контента - обновляем для работы с новым HTMLBlockNode
const insertStyledHtmlBlock = (editor: any, htmlContent: string) => {
  try {
    // Очищаем HTML от потенциально опасных элементов с помощью DOMPurify
    const safeHtml = DOMPurify.sanitize(htmlContent, {
      ALLOWED_TAGS: [
        'p', 'div', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'a', 'strong', 'em', 'blockquote',
        'code', 'pre', 'img', 'svg', 'path', 'figure', 'figcaption',
        'table', 'tr', 'td', 'th', 'thead', 'tbody', 'time',
        'mark', 'hr', 'br'
      ],
      ALLOWED_ATTR: [
        'class', 'style', 'id', 'href', 'src', 'alt', 'title',
        'width', 'height', 'fill', 'stroke', 'viewBox', 'xmlns',
        'strokeWidth', 'strokeLinecap', 'strokeLinejoin',
        'fill-rule', 'clip-rule', 'd', 'transform', 'data-*'
      ],
      ADD_TAGS: ['svg', 'path', 'circle', 'rect', 'line', 'g', 'polyline', 'polygon'],
      ALLOW_DATA_ATTR: true
    });
    
    // Очистка от лишних пробелов и переносов строк
    const cleanHtml = safeHtml
      .replace(/\n\s+/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
    
    // Создаем временный div для анализа структуры HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = cleanHtml;
    
    // Извлекаем атрибуты из корневого элемента, если это div
    const rootElement = tempDiv.firstElementChild;
    const attrs: Record<string, any> = {
      'data-type': 'html-block'
    };
    
    if (rootElement) {
      if (rootElement.className) attrs.class = rootElement.className;
      if (rootElement.getAttribute('style')) attrs.style = rootElement.getAttribute('style');
      if (rootElement.id) attrs.id = rootElement.id;
    }
    
    // Создаем HTMLBlockNode с извлеченными атрибутами
    const htmlBlockNode = {
      type: 'htmlBlock',
      attrs: attrs,
      content: []
    };
    
    // Вставляем внутреннее содержимое как HTML
    // Это позволит редактору правильно распарсить и вставить содержимое
    editor.chain().focus().insertContent({
      type: 'htmlBlock',
      attrs: attrs,
      content: editor.schema.nodeFromJSON({
        type: 'paragraph',
        content: [
          {
            type: 'text',
            text: ' ' // Пробел для обеспечения редактируемости
          }
        ]
      })
    }).run();
    
    // После вставки блока, в следующем тике вставляем HTML-содержимое
    setTimeout(() => {
      // Очищаем содержимое блока
      const selection = editor.state.selection;
      const nodePos = selection.$anchor.pos - 1;
      
      // Вставляем обработанный HTML
      editor.commands.insertContentAt(nodePos, cleanHtml);
      
      // Фокусируемся на редакторе
      editor.commands.focus();
    }, 0);
    
    return true;
  } catch (error) {
    console.error('Ошибка при вставке HTML-блока:', error);
    
    // Запасной вариант - вставляем просто текст
    try {
      const plainText = htmlContent.replace(/<[^>]*>/g, ' ').trim();
      editor.chain().focus().insertContent(plainText).run();
    } catch (fallbackError) {
      console.error('Ошибка при вставке текста:', fallbackError);
    }
    
    return false;
  }
};

// Добавляем метод в расширение для доступа из компонентов
const insertStyledHtmlBlockExtension = Extension.create({
  name: 'insertStyledHtmlBlock',
  
  addCommands() {
    return {
      insertStyledHtmlBlock: (htmlContent: string) => ({ editor }) => {
        return insertStyledHtmlBlock(editor, htmlContent);
      }
    };
  }
});

// Добавляем новое расширение для защиты блочных компонентов от удаления с клавиатуры
const BlockDeletionProtection = Extension.create({
  name: 'blockDeletionProtection',
  
  addKeyboardShortcuts() {
    // Список типов узлов, которые нужно защитить от удаления
    const protectedNodeTypes = [
      'kanbanBoard', // Тип узла канбан-доски
      'htmlBlock',   // Тип HTML-блока
      // Добавьте другие типы защищаемых блоков здесь
    ];
    
    // Функция для проверки, является ли узел защищенным
    const isProtectedNode = (node: any): boolean => {
      return protectedNodeTypes.includes(node.type?.name) || 
            (node.attrs && node.attrs['data-type'] === 'kanban');
    };
    
    return {
      // Перехватываем клавиши Backspace и Delete
      Backspace: () => {
        const { selection } = this.editor.state;
        const { empty, $anchor } = selection;
        
        // Если выделение пустое, проверяем текущий узел
        if (empty) {
          // Получаем родительский узел
          const parentNode = $anchor.parent;
          
          // Если курсор находится в начале параграфа, проверяем предыдущий узел
          if ($anchor.parentOffset === 0) {
            const nodeBefore = this.editor.state.doc.resolve($anchor.pos - 1).nodeBefore;
            
            // Проверяем, является ли предыдущий узел блочным компонентом, который нужно защитить
            if (nodeBefore && isProtectedNode(nodeBefore)) {
              console.log('Предотвращено удаление защищенного блочного компонента:', nodeBefore.type.name);
              return true; // Предотвращаем удаление
            }
          }
        } else {
          // Для неустановленного курсора нужно проверить, есть ли в выделении блочные компоненты
          const { $from, $to } = selection;
          let hasProtectedNode = false;
          
          // Перебираем узлы в выделении
          this.editor.state.doc.nodesBetween($from.pos, $to.pos, (node) => {
            if (isProtectedNode(node)) {
              hasProtectedNode = true;
              return false; // Прекращаем перебор
            }
            return true; // Продолжаем перебор
          });
          
          if (hasProtectedNode) {
            console.log('Предотвращено удаление защищенного блочного компонента внутри выделения');
            return true; // Предотвращаем удаление
          }
        }
        
        // Если это не защищенный узел, позволяем стандартную обработку
        return false;
      },
      
      // Аналогичная логика для клавиши Delete
      Delete: () => {
        const { selection } = this.editor.state;
        const { empty, $anchor } = selection;
        
        if (empty) {
          const parentNode = $anchor.parent;
          
          // Если курсор находится в конце параграфа, проверяем следующий узел
          if ($anchor.parentOffset === parentNode.content.size) {
            const nodeAfter = this.editor.state.doc.resolve($anchor.pos + 1).nodeAfter;
            
            if (nodeAfter && isProtectedNode(nodeAfter)) {
              console.log('Предотвращено удаление защищенного блочного компонента:', nodeAfter.type.name);
              return true; // Предотвращаем удаление
            }
          }
        } else {
          // Для выделения проверяем наличие защищенных узлов
          const { $from, $to } = selection;
          let hasProtectedNode = false;
          
          this.editor.state.doc.nodesBetween($from.pos, $to.pos, (node) => {
            if (isProtectedNode(node)) {
              hasProtectedNode = true;
              return false;
            }
            return true;
          });
          
          if (hasProtectedNode) {
            console.log('Предотвращено удаление защищенного блочного компонента внутри выделения');
            return true;
          }
        }
        
        // Если это не защищенный узел, позволяем стандартную обработку
        return false;
      }
    };
  }
});

// Добавляем стили для кнопки удаления канбан-доски

interface RichTextEditorProps {
  content: string;
  onSave: (contentToSave?: string, targetFolderId?: string | null, fileName?: string) => void;
  onChange: (value: string) => void;
  title?: string;
  withBackground?: boolean;
  format?: 'html' | 'markdown';
  itemId?: string;
  onEdit?: () => void;
  isEditing?: boolean;
  autoSave?: boolean;
  fileType?: string;
  onUpdateTitle?: (newTitle: string) => void;
  onTitleInputChange?: (newTitle: string) => void;
  onIdChange?: (oldId: string, newId: string) => void; // Новый пропс для обработки смены ID
}

// Расширяем интерфейс для пользовательских событий
interface CustomEventMap {
  'block-menu-open': CustomEvent<{
    x: number;
    y: number;
    position: number;
  }>;
}

declare global {
  interface WindowEventMap extends CustomEventMap {}
}

// Расширяем типы команд TipTap
declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    insertStyledHtmlBlock: {
      /**
       * Вставляет HTML блок с сохранением стилей
       */
      insertStyledHtmlBlock: (html: string) => ReturnType;
    };
  }
}

// Добавляем расширение для управления таблицами
const TableControls = Extension.create({
  name: 'tableControls',
  
  addProseMirrorPlugins() {
    const plugin = new Plugin({
      key: new PluginKey('tableControls'),
      
      view: () => {
        let tableControls: HTMLElement | null = null;
        let activeTable: HTMLElement | null = null;
        
        // Функция для создания элемента контролов
        const createTableControls = (table: HTMLElement) => {
          // Удаляем старые контролы, если они есть
          if (tableControls) {
            document.body.removeChild(tableControls);
          }
          
          // Создаем новый контейнер для контролов
          tableControls = document.createElement('div');
          tableControls.className = 'table-controls top-controls';
          
          // Добавляем кнопки для контролов
          const addRowButton = document.createElement('button');
          addRowButton.className = 'table-control-btn';
          addRowButton.title = 'Добавить строку';
          addRowButton.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 10h18M12 3v14"></path></svg>';
          addRowButton.addEventListener('click', () => {
            const { state } = this.editor.view;
            this.editor.chain().focus().addRowAfter().run();
          });
          
          const deleteRowButton = document.createElement('button');
          deleteRowButton.className = 'table-control-btn';
          deleteRowButton.title = 'Удалить строку';
          deleteRowButton.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 10h18M12 15l-3-3m0 0l3-3m-3 3h6"></path></svg>';
          deleteRowButton.addEventListener('click', () => {
            this.editor.chain().focus().deleteRow().run();
          });
          
          const addColumnButton = document.createElement('button');
          addColumnButton.className = 'table-control-btn';
          addColumnButton.title = 'Добавить столбец';
          addColumnButton.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 3v18M3 12h14"></path></svg>';
          addColumnButton.addEventListener('click', () => {
            this.editor.chain().focus().addColumnAfter().run();
          });
          
          const deleteColumnButton = document.createElement('button');
          deleteColumnButton.className = 'table-control-btn';
          deleteColumnButton.title = 'Удалить столбец';
          deleteColumnButton.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 3v18M15 12l-3-3m0 0l-3 3m3-3v6"></path></svg>';
          deleteColumnButton.addEventListener('click', () => {
            this.editor.chain().focus().deleteColumn().run();
          });
          
          const mergeCellsButton = document.createElement('button');
          mergeCellsButton.className = 'table-control-btn';
          mergeCellsButton.title = 'Объединить ячейки';
          mergeCellsButton.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M16 8H8V4h8v4zM16 20H8v-4h8v4zM10 12h4M3 12h3M18 12h3"></path></svg>';
          mergeCellsButton.addEventListener('click', () => {
            this.editor.chain().focus().mergeCells().run();
          });
          
          const splitCellButton = document.createElement('button');
          splitCellButton.className = 'table-control-btn';
          splitCellButton.title = 'Разделить ячейку';
          splitCellButton.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 4v16M4 14h16M4 8h8M12 20h8"></path></svg>';
          splitCellButton.addEventListener('click', () => {
            this.editor.chain().focus().splitCell().run();
          });
          
          // Дополнительная кнопка для переключения заголовка строки
          const toggleHeaderRowButton = document.createElement('button');
          toggleHeaderRowButton.className = 'table-control-btn';
          toggleHeaderRowButton.title = 'Переключить заголовок строки';
          toggleHeaderRowButton.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6h16M4 10h16M4 14h16M4 18h16"/></svg>';
          toggleHeaderRowButton.addEventListener('click', () => {
            this.editor.chain().focus().toggleHeaderRow().run();
          });
          
          // Дополнительная кнопка для переключения заголовка столбца
          const toggleHeaderColumnButton = document.createElement('button');
          toggleHeaderColumnButton.className = 'table-control-btn';
          toggleHeaderColumnButton.title = 'Переключить заголовок столбца';
          toggleHeaderColumnButton.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 4v16M10 4v16M14 4v16M18 4v16"/></svg>';
          toggleHeaderColumnButton.addEventListener('click', () => {
            this.editor.chain().focus().toggleHeaderColumn().run();
          });
          
          // Дополнительная кнопка для удаления таблицы
          const deleteTableButton = document.createElement('button');
          deleteTableButton.className = 'table-control-btn';
          deleteTableButton.title = 'Удалить таблицу';
          deleteTableButton.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6"/></svg>';
          deleteTableButton.addEventListener('click', () => {
            this.editor.chain().focus().deleteTable().run();
          });
          
          // Создаем разделители
          const divider1 = document.createElement('div');
          divider1.className = 'divider';
          
          const divider2 = document.createElement('div');
          divider2.className = 'divider';
          
          // Добавляем все кнопки в контейнер
          tableControls.appendChild(addRowButton);
          tableControls.appendChild(deleteRowButton);
          tableControls.appendChild(addColumnButton);
          tableControls.appendChild(deleteColumnButton);
          tableControls.appendChild(divider1);
          tableControls.appendChild(mergeCellsButton);
          tableControls.appendChild(splitCellButton);
          tableControls.appendChild(toggleHeaderRowButton);
          tableControls.appendChild(toggleHeaderColumnButton);
          tableControls.appendChild(divider2);
          tableControls.appendChild(deleteTableButton);
          
          // Добавляем контролы на страницу
          document.body.appendChild(tableControls);
          
          return tableControls;
        };
        
        // Функция для позиционирования контролов
        const positionTableControls = (table: HTMLElement) => {
          if (!tableControls) return;
          
          const tableRect = table.getBoundingClientRect();
          
          tableControls.style.top = `${window.scrollY + tableRect.top - 40}px`;
          tableControls.style.left = `${window.scrollX + tableRect.left}px`;
        };
        
        // Функция для проверки таблицы и отображения контролов
        const checkForTable = (view: any) => {
          const { state } = view;
          const { selection } = state;
          
          // Проверяем, находимся ли мы в таблице
          const inTable = !!selection.$anchor.node(-1)?.type.spec.tableRole ||
                        !!selection.$anchor.node(-2)?.type.spec.tableRole ||
                        selection.$anchor.node(1)?.type.name === 'table' ||
                        selection.$anchor.node(2)?.type.name === 'table';
          
          if (inTable) {
            // Находим элемент таблицы в DOM
            const dom = view.dom;
            const tables = dom.querySelectorAll('table');
            
            for (const table of tables) {
              const tablePos = view.posAtDOM(table, 0);
              
              // Проверяем, находится ли курсор внутри данной таблицы
              if (tablePos <= selection.$anchor.pos && 
                  selection.$anchor.pos <= tablePos + table.nodeSize) {
                
                // Добавляем класс для стилизации активной таблицы
                table.classList.add('has-focus');
                
                // Если у таблицы нет атрибута tabindex, добавляем его для доступности
                if (!table.getAttribute('tabindex')) {
                  table.setAttribute('tabindex', '0');
                }
                
                activeTable = table;
                
                // Создаем или перемещаем контролы
                if (!tableControls) {
                  tableControls = createTableControls(table);
                }
                
                positionTableControls(table);
                return;
              }
            }
          }
          
          // Если мы не в таблице, скрываем контролы и удаляем класс активной таблицы
          if (activeTable) {
            activeTable.classList.remove('has-focus');
            activeTable = null;
          }
          
          if (tableControls) {
            document.body.removeChild(tableControls);
            tableControls = null;
          }
        };
        
        return {
          update: (view: any) => {
            checkForTable(view);
          },
          
          destroy: () => {
            if (tableControls) {
              document.body.removeChild(tableControls);
              tableControls = null;
            }
            
            if (activeTable) {
              activeTable.classList.remove('has-focus');
              activeTable = null;
            }
          }
        };
      }
    });
    
    return [plugin];
  }
});

// Создаем собственное расширение для ссылок с поддержкой дополнительных атрибутов
const CustomLink = Link.configure({
  openOnClick: false,
  HTMLAttributes: {
    class: 'custom-link text-blue-600 dark:text-blue-400 hover:underline',
  },
}).extend({
  // Приоритет должен быть выше, чем у встроенного Link в StarterKit
  priority: 1000,
  addAttributes() {
    return {
      ...this.parent?.(),
      // Добавляем дополнительные атрибуты для внутренних ссылок
      'data-internal-link': {
        default: null,
        renderHTML: attributes => {
          if (!attributes['data-internal-link']) {
            return {};
          }
          return {
            'data-internal-link': attributes['data-internal-link']
          };
        },
        parseHTML: element => element.getAttribute('data-internal-link')
      },
      'data-document-id': {
        default: null,
        renderHTML: attributes => {
          if (!attributes['data-document-id']) {
            return {};
          }
          return {
            'data-document-id': attributes['data-document-id']
          };
        },
        parseHTML: element => element.getAttribute('data-document-id')
      },
      'data-document-name': {
        default: null,
        renderHTML: attributes => {
          if (!attributes['data-document-name']) {
            return {};
          }
          return {
            'data-document-name': attributes['data-document-name']
          };
        },
        parseHTML: element => element.getAttribute('data-document-name')
      }
    };
  }
});



export function RichTextEditor({
  content,
  onSave,
  onChange,
  title,
  withBackground = true,
  format = 'html',
  itemId,
  onEdit,
  isEditing = true,
  autoSave = true,
  fileType = 'article',
  onUpdateTitle,
  onTitleInputChange,
  onIdChange
}: RichTextEditorProps) {
  const [showBlockSelector, setShowBlockSelector] = useState(false);
  const [blockSelectorPosition, setBlockSelectorPosition] = useState({ x: 0, y: 0 });
  const [selectionCoords, setSelectionCoords] = useState<{ x: number; y: number } | null>(null);

  const floatingButtonRef = useRef<HTMLButtonElement>(null);
  const editorContentRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  
  // Обновляем состояние для предпросмотра ссылок, чтобы оно содержало только URL
  const [linkPreview, setLinkPreview] = useState<{
    url: string;
    position: { x: number; y: number };
    visible: boolean;
  } | null>(null);
  
  const [internalLinkPreview, setInternalLinkPreview] = useState<{
    url: string;
    position: { x: number; y: number };
    visible: boolean;
  } | null>(null);
  
  // Ссылка для хранения последней выбранной ссылки (для удаления)
  const lastSelectedLinkRef = useRef<HTMLAnchorElement | null>(null);
  
  // Таймер для задержки показа превью
  const linkPreviewTimer = useRef<NodeJS.Timeout | null>(null);
  
  // Состояние для отображения меню блока (4 точек)
  const [showHtmlBlockSelector, setShowHtmlBlockSelector] = useState(false);
  const [htmlBlockSelectorPosition, setHtmlBlockSelectorPosition] = useState({ x: 0, y: 0 });
  
  // Состояние для текущего контента и флаг изменений
  const [currentContent, setCurrentContent] = useState(content);
  const [hasChanges, setHasChanges] = useState(false);
  const [lastSavedContent, setLastSavedContent] = useState<string>(content);
  
  // Используем useRef для передачи всегда актуального состояния в хук
  const currentContentRef = useRef<string>(currentContent);
  
  // Обновляем ref при изменении состояния
  useEffect(() => {
    currentContentRef.current = currentContent;
  }, [currentContent]);
  
  // Получаем доступ к репозиторию базы данных
  const { repository, isInitialized } = useDatabase();
  
  // Улучшенный хук для отслеживания обновлений и предотвращения цикличности
  const [updateTrigger, setUpdateTrigger] = useState(0);
  
  // В начале компонента, добавим состояние для отслеживания изменений канбана
  const [kanbanChanging, setKanbanChanging] = useState<boolean>(false);
  const [lastKanbanId, setLastKanbanId] = useState<string | null>(null);
  
  // Добавим новую переменную для отслеживания изменений контента
  const [contentChanged, setContentChanged] = useState<boolean>(false);
  
  // Обновляем onUpdate метод редактора
  const editor = useEditor({
    extensions: [
      // Используем наше расширение вместо стандартного Link
      CustomLink,
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        },
        // Не нужно отключать встроенный Link, CustomLink переопределит его благодаря более высокому приоритету
        blockquote: {
          HTMLAttributes: {
            class: 'border-l-4 border-gray-300 dark:border-gray-600 pl-4 my-4'
          }
        },
        code: {
          HTMLAttributes: {
            class: 'bg-gray-100 dark:bg-gray-800 rounded px-1 py-0.5 font-mono'
          }
        },
        codeBlock: false,
        paragraph: {
          HTMLAttributes: ({ node }: { node: { content: { size: number } } }) => ({
            class: `text-gray-900 dark:text-gray-100 leading-normal ${node.content.size === 0 ? 'is-empty' : ''}`,
          }),
        }
      }),
      Placeholder.configure({
        placeholder: ({ node }) => {
          // Не показываем плейсхолдер, так как мы используем CSS
          return '';
        },
        emptyEditorClass: 'is-editor-empty',
        emptyNodeClass: 'is-empty',
        showOnlyWhenEditable: true,
        includeChildren: true,
      }),
      NewLineHandling,
      BlockDeletionProtection,
      CodeBlockLowlight.configure({
        lowlight: lowlightPlugin,
        HTMLAttributes: {
          class: 'bg-gray-100 dark:bg-gray-800 rounded-lg p-4 font-mono my-4'
        }
      }),
      // Заменяем стандартное расширение Link на наше кастомное
      // Link.configure({
      //   openOnClick: false,
      //   HTMLAttributes: {
      //     class: 'text-blue-600 dark:text-blue-400 hover:underline'
      //   }
      // }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'border-collapse table-auto w-full my-4'
        }
      }),
      TableRow.configure({
        HTMLAttributes: {
          class: 'border-b dark:border-gray-700'
        }
      }),
      TableHeader.configure({
        HTMLAttributes: {
          class: 'border-b-2 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 font-bold p-2 text-left'
        }
      }),
      TableCell.configure({
        HTMLAttributes: {
          class: 'border dark:border-gray-700 p-2'
        }
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'rounded-lg max-w-full'
        }
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
        HTMLAttributes: {
          class: 'text-orange-500 dark:text-orange-400 bg-transparent',
        }
      }),
      Typography,
      Underline,
      Subscript,
      Superscript,
      TaskList.configure({
        HTMLAttributes: {
          class: 'not-prose pl-0'
        }
      }),
      TaskItem.configure({
        HTMLAttributes: {
          class: 'flex items-start my-2'
        },
        nested: true
      }),
      AllowClassesOnNodes,
      HtmlSupport.configure({
        rules: [
          {
            // Применяем только к блочным элементам, исключаем text
            types: [
              'paragraph', 
              'heading',
              'bulletList',
              'orderedList', 
              'listItem',
              'blockquote',
              'codeBlock',
              'horizontalRule',
              'image',
              'taskList',
              'taskItem',
              'table',
              'tableRow',
              'tableCell',
              'tableHeader',
              'htmlBlock',
              'kanbanBoard'
            ],
            attributes: {
              class: {
                default: null,
                parseHTML: (element: HTMLElement) => element.getAttribute('class'),
                renderHTML: (attributes: { class?: string }) => {
                  if (!attributes.class) return {};
                  return { class: attributes.class };
                },
              },
              style: {
                default: null,
                parseHTML: (element: HTMLElement) => element.getAttribute('style'),
                renderHTML: (attributes: { style?: string }) => {
                  if (!attributes.style) return {};
                  return { style: attributes.style };
                },
              },
              id: {
                default: null,
                parseHTML: (element: HTMLElement) => element.getAttribute('id'),
                renderHTML: (attributes: { id?: string }) => {
                  if (!attributes.id) return {};
                  return { id: attributes.id };
                },
              },
              'data-type': {
                default: null,
                parseHTML: (element: HTMLElement) => element.getAttribute('data-type'),
                renderHTML: (attributes: { 'data-type'?: string }) => {
                  if (!attributes['data-type']) return {};
                  return { 'data-type': attributes['data-type'] };
                },
              },
              'data-state': {
                default: null,
                parseHTML: (element: HTMLElement) => element.getAttribute('data-state'),
                renderHTML: (attributes: { 'data-state'?: string }) => {
                  if (!attributes['data-state']) return {};
                  return { 'data-state': attributes['data-state'] };
                },
              }
            }
          }
        ],
      }),
      HTMLBlockNode, // Используем новый HTMLBlockNode вместо BlockContainer
      insertStyledHtmlBlockExtension, // Добавляем наше расширение
      EnhancedPasteHandler, // Добавляем расширение для обработки вставки HTML
      InteractiveKanbanNode,
      TableControls,  // Добавляем наше расширение для управления таблицами
      Markdown, // Добавляем поддержку markdown
    ],
    content,
    editable: true,
    onUpdate: ({ editor, transaction }) => {
      const html = editor.getHTML();
      
      // Проверяем, содержит ли контент канбан
      if (html.includes('data-type="kanban"') || html.includes('data-type="kanbanBoard"') || html.includes('interactive-kanban-wrapper')) {
        console.log('[Редактор] Обнаружен канбан в документе, обновляем только локальное состояние без сохранения');
        setCurrentContent(html);
        return;
      }
      
      // Проверяем, запрещено ли автосохранение для этой транзакции по другим причинам
      if (transaction.getMeta('skipAutoSave')) {
        // Только обновляем локальное состояние, не вызываем onChange
        setCurrentContent(html);
        return;
      }
      
      // Всегда обновляем локальное состояние
      setCurrentContent(html);
      
      // Проверка на изменения содержимого
      const isChanged = html !== currentContent || transaction.docChanged;
      
      if (isChanged) {
        // Устанавливаем флаг наличия изменений
        setHasChanges(true);
        setContentChanged(true);
        
        // Уведомляем родителя об изменениях
        onChange(html);
        
        // Запускаем триггер для сохранения
        setUpdateTrigger(prev => prev + 1);
      }
    },
    autofocus: 'end',
  });
  
  // ПЕРЕМЕЩЕНО: Функция для получения актуального контента после инициализации editor
  const getActualContent = useCallback(() => {
    // Важно: берем HTML прямо из редактора, так как currentContent может отставать
    if (editor && !editor.isDestroyed) {
      const editorHtml = editor.getHTML();
      return editorHtml;
    }
    
    return currentContent;
  }, [currentContent, content, editor, hasChanges]);
  
  // Инициализация автосохранения - передаем getActualContent для получения актуального значения
  // ИСПРАВЛЕНИЕ: Отключаем автосохранение для временных файлов
  const shouldAutoSave = autoSave && (!itemId || !itemId.startsWith('temp-'));
  
  useAutoSave(
    content, // Начальное значение (будет переопределено функцией getActualContent)
    async (contentToSave: string, newId?: string) => {
      // ОБРАБОТКА СМЕНЫ ID ДЛЯ ВРЕМЕННЫХ ФАЙЛОВ
      if (newId && itemId && itemId.startsWith('temp-') && onIdChange) {
        console.log('[RichTextEditor AutoSave] Получен новый ID:', newId, 'для временного файла:', itemId);
        
        // Вызываем обработчик смены ID
        onIdChange(itemId, newId);
        
        return { success: true, timestamp: Date.now() };
      }
      
      // ОБЫЧНАЯ ЛОГИКА СОХРАНЕНИЯ
      // Проверяем, содержит ли контент канбан (который не нужно сохранять)
      if (contentToSave.includes('data-type="kanban"') || 
          contentToSave.includes('data-type="kanbanBoard"') || 
          contentToSave.includes('interactive-kanban-wrapper')) {
        console.log('[AutoSave] Обнаружен канбан, пропускаем сохранение');
        return { success: true, timestamp: Date.now() };
      }

      try {
        // Проверяем, изменился ли контент по сравнению с последним сохраненным
        if (contentToSave !== lastSavedContent) {
          // Вызываем сохранение через родительский обработчик
          onChange(contentToSave);
          
          // Если есть onSave и itemId, вызываем его напрямую, но не ждем завершения 
          // для предотвращения перерисовки
          if (onSave && itemId) {
            console.log('[AutoSave] Вызов onSave для', itemId);
            // ИСПРАВЛЕНИЕ: Передаем актуальный контент в onSave
            onSave(contentToSave);
          }
          
          // Обновляем lastSavedContent без обновления UI
          setLastSavedContent(contentToSave);
          setHasChanges(false);
          
          return { success: true, timestamp: Date.now() };
        } else {
          console.log('[AutoSave] Контент не изменился, пропускаем сохранение');
          return { success: true, timestamp: Date.now() };
        }
      } catch (error) {
        console.error('[AutoSave] Ошибка при сохранении:', error);
        return { success: false, timestamp: Date.now() };
      }
    },
    800,
    shouldAutoSave, // ИСПРАВЛЕНИЕ: Используем shouldAutoSave вместо autoSave
    itemId,
    getActualContent // Передаем функцию для получения актуального контента
  );
  
  // Заглушка определения бездействия пользователя
  const { isIdle } = useIdleDetection(
    800,
    () => {}, // Пустая функция вместо handleIdleSave
    ['keydown']
  );

  // Фокус на последний пустой параграф при монтировании
  useEffect(() => {
    if (editor) {
      editor.setEditable(true);
      
      // Проверяем, пуст ли редактор
      if (editor.isEmpty) {
        // Добавляем начальный пустой параграф, если редактор пуст
        editor.commands.setContent('<p></p>');
        editor.commands.focus('end');
      }
      
      // Вместо добавления обработчика событий DOM, мы используем useEffect с зависимостью от редактора
      // Это предотвратит дополнительные циклы обновления
    }
  }, [editor]); // Возвращаем только редактор как зависимость

  // Флаги для предотвращения циклов
  const isLoadingFromDB = useRef(false);
  const documentLoadCounter = useRef(0);
  
  // Загружаем содержимое из RxDB при первоначальной загрузке
  useEffect(() => {
    const loadContentFromDB = async () => {
      // Защита от повторных вызовов
      if (!itemId || !isInitialized || isLoadingFromDB.current) return;
      
      // Отслеживание количества загрузок для одного документа
      if (documentLoadCounter.current > 2) {
        console.warn(`RichTextEditor: Слишком много загрузок документа ${itemId}, пропускаем для избежания цикла`);
        return;
      }
      
      documentLoadCounter.current++;
      isLoadingFromDB.current = true;
      
      try {
        const doc = await repository.getDocumentById(itemId);
        
        if (doc && doc.content) {
          // Более строгое сравнение, для избежания циклов
          let shouldUpdate = false;
          
          if (typeof doc.content === 'string' && typeof currentContent === 'string') {
            // Нормализуем строки перед сравнением
            const normalizeHtml = (html: string) => {
              return html
                .trim()
                .replace(/\s+/g, ' ')
                .replace(/>\s+</g, '><')
                .replace(/\s+>/g, '>')
                .replace(/<\s+/g, '<');
            };
            
            const normalizedDoc = normalizeHtml(doc.content);
            const normalizedCurrent = normalizeHtml(currentContent);
            
            shouldUpdate = normalizedDoc !== normalizedCurrent;
          } else {
            // Для других типов данных используем JSON сравнение
            shouldUpdate = JSON.stringify(doc.content) !== JSON.stringify(currentContent);
          }
          
          if (shouldUpdate) {
            console.log(`Обновление содержимого редактора из локальной БД`);
            
            // Обновляем содержимое редактора и состояние компонента
            if (editor && !editor.isDestroyed) {
                          // Для конспектов (notes) и всех остальных типов - стандартная обработка
            // Бэкенд теперь правильно декодирует HTML entities при сохранении
            console.log('[RichTextEditor БД] Устанавливаем контент из БД');
            editor.commands.setContent(doc.content);
            }
            
            // Обновляем локальное состояние
            setCurrentContent(doc.content);
            setLastSavedContent(doc.content);
            setHasChanges(false);
          }
        }
      } catch (error) {
        console.error(`RichTextEditor: Ошибка при загрузке документа ${itemId} из БД:`, error);
      } finally {
        isLoadingFromDB.current = false;
        
        // Сбрасываем счетчик загрузок при размонтировании
        setTimeout(() => {
          documentLoadCounter.current = 0;
        }, 1000);
      }
    };
    
    loadContentFromDB();
  }, [itemId, repository, isInitialized, editor, fileType]); // Добавляем fileType для обработки конспектов

  // Сохраняем содержимое при обновлении props
  useEffect(() => {
    console.log('[DEBUG useEffect] Проверка условий:', {
      hasEditor: !!editor,
      content: content?.substring(0, 50) + '...',
      contentLength: content?.length,
      fileType: fileType,
      itemId: itemId
    });
    
    if (editor) {
      const editorContent = editor.getHTML();
      console.log('[DEBUG useEffect] editorContent vs content:', {
        editorContent: editorContent?.substring(0, 50) + '...',
        content: content?.substring(0, 50) + '...',
        areEqual: editorContent === content,
        isEmpty: content === '' && editorContent !== '<p></p>'
      });
      
      // Обновляем содержимое при любом изменении content или itemId, включая очистку content
      // Обновляем также, если контент стал пустым (при выборе пустого файла)
      // ВАЖНО: Для конспектов (notes) всегда обрабатываем, даже если контент равен
      const shouldUpdate = editorContent !== content || 
                          (content === '' && editorContent !== '<p></p>') ||
                          (fileType === 'notes' && content);
      
      if (shouldUpdate) {
        console.log('Обновление содержимого редактора из-за изменения props content');
        
        // Для конспектов (notes) и всех остальных типов - стандартная обработка
        // Бэкенд теперь правильно декодирует HTML entities при сохранении
        console.log('[RichTextEditor] Устанавливаем контент');
        editor.commands.setContent(content || '<p></p>');
        
        // Сбрасываем состояние редактора при смене файла
        setCurrentContent(content);
        setLastSavedContent(content);
        setHasChanges(false);
        
        // Прокручиваем содержимое в начало при загрузке нового файла
        if (editorContentRef.current) {
          editorContentRef.current.scrollTop = 0;
        }
        
        // Прокручиваем всю страницу в начало при загрузке нового файла
        window.scrollTo(0, 0);
      }
    }
  }, [content, itemId, editor, fileType]);

  // Обновляем локальное состояние, когда извне приходит новый контент
  useEffect(() => {
    // Даже если есть изменения, но контент полностью изменился (сменился файл),
    // всё равно обновляем локальное состояние
    const contentChanged = content !== currentContent;
    const isContentReset = !content && currentContent; // Обрабатываем случай, когда content стал пустым
    
    if (contentChanged && (isContentReset || !hasChanges)) {
      setCurrentContent(content);
      setLastSavedContent(content);
      setHasChanges(false);
    }
  }, [content, currentContent, hasChanges]);

  // Добавляем обработчик события выделения текста и клика на иконку
  useEffect(() => {
    if (!editor || !editorContentRef.current) return;

    // Функция для определения координат выделения
    const handleSelectionChange = () => {
      if (editor.view.hasFocus() && !editor.state.selection.empty) {
        // Получаем текущее выделение
        const { from, to } = editor.state.selection;
        
        // Если есть выделение
        if (from !== to) {
          try {
            const view = editor.view;
            
            // Используем координаты из ProseMirror view для более точного позиционирования
            const startCoords = view.coordsAtPos(from);
            const endCoords = view.coordsAtPos(to);
            
            // Используем центр выделения
            const centerX = (startCoords.left + endCoords.right) / 2;
            
            // Определяем высоту возможных header элементов
            const navbar = document.querySelector('nav, .navbar, .header, [role="banner"]');
            const navbarHeight = navbar ? navbar.getBoundingClientRect().height : 0;
            
            // Учитываем высоту navbar в расчете позиции
            const topOffset = startCoords.top - 80 - navbarHeight; // Увеличиваем отступ до 80px + высота navbar
            
            
            setSelectionCoords({ 
              x: centerX, 
              y: topOffset 
            });
            
            // Метод резервный: DOM selection (оставляем для сравнения)
            const selection = window.getSelection();
            if (selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              const rect = range.getBoundingClientRect();
              
            }
            
          } catch (error) {
            console.error('Error getting selection coordinates:', error);
            setSelectionCoords(null);
          }
        }
      } else {
        // Если нет выделения, скрываем панель
        setSelectionCoords(null);
      }
    };

    // Обработчик клика на иконку меню (4 точек) перед параграфом
    const handleEditorClick = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      const target = mouseEvent.target as HTMLElement;
      const rect = target.getBoundingClientRect();
      
      // Определяем, был ли клик на иконке меню (перед параграфом)
      // Проверяем позицию клика относительно левого края параграфа
      if (target.tagName === 'P' && mouseEvent.clientX < rect.left && mouseEvent.clientX > rect.left - 30) {
        // Находим позицию ноды в редакторе
        const pos = editor.view.posAtDOM(target, 0);
        if (pos !== null) {
          // Устанавливаем выделение на этот параграф
          editor.commands.setTextSelection(pos);
          
          // Открываем HtmlBlockSelector вместо BlockSelector
          setHtmlBlockSelectorPosition({ 
            x: rect.left, 
            y: rect.top 
          });
          setShowHtmlBlockSelector(true);
          
          mouseEvent.preventDefault();
          mouseEvent.stopPropagation();
        }
      }
    };

    // Комбинированный обработчик для события клика
    const handleCombinedClick = (e: Event) => {
      // Убираем setTimeout и вызываем напрямую
      handleSelectionChange();
      handleEditorClick(e);
    };

    // Добавляем слушатель события selectionchange
    document.addEventListener('selectionchange', handleSelectionChange);
    
    // Добавляем слушатель клика для проверки выделения и для клика на иконке 4 точек
    const editorEl = editorContentRef.current.querySelector('.ProseMirror');
    editorEl?.addEventListener('click', handleCombinedClick);

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      const editorEl = editorContentRef.current?.querySelector('.ProseMirror');
      editorEl?.removeEventListener('click', handleCombinedClick);
    };
  }, [editor]);

  // Добавляем обработчик события 'kanban-state-change' внутри useEffect, где инициализируется редактор
  useEffect(() => {
    // Если редактор не инициализирован, выходим
    if (!editor || !editor.view || !editor.view.dom) {
      return;
    }
    
    // Обработчик события kanban-state-change
    const handleKanbanStateChange = (event: CustomEvent) => {
      console.log('[Редактор] Обнаружен канбан в документе, обновляем только локальное состояние без сохранения');
      setKanbanChanging(true);
      setLastKanbanId(event.detail?.componentId || null);
      
      // Устанавливаем таймер для сброса флага
      setTimeout(() => {
        setKanbanChanging(false);
      }, 3000); // даем немного времени для завершения операции
    };

    // Добавляем слушатель события
    editor.view.dom.addEventListener('kanban-state-change', handleKanbanStateChange as EventListener);

    // Возвращаем функцию очистки
    return () => {
      // Удаляем слушатель события
      editor.view.dom.removeEventListener('kanban-state-change', handleKanbanStateChange as EventListener);
    };
  }, [editor]); // Зависимости useEffect

  const handleExportPDF = () => {
    if (!editor) return;

    const element = document.createElement('div');
    element.className = 'prose max-w-none p-8';
    element.style.color = '#000000';
    
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      * {
        color: #000000 !important;
      }
      h1, h2, h3, h4, h5, h6 {
        color: #000000 !important;
        margin-bottom: 1em;
      }
      p {
        color: #000000 !important;
        margin-bottom: 0.5em;
      }
      a {
        color: #2563eb !important;
        text-decoration: underline;
      }
    `;
    element.appendChild(styleElement);
    
    const titleElement = document.createElement('h1');
    titleElement.style.fontSize = '24px';
    titleElement.style.fontWeight = 'bold';
    titleElement.style.marginBottom = '24px';
    titleElement.style.color = '#000000';
    titleElement.textContent = title || 'Документ';
    
    element.appendChild(titleElement);
    
    const contentDiv = document.createElement('div');
    contentDiv.innerHTML = editor.getHTML();
    element.appendChild(contentDiv);

    const documentTitle = title || 'document';
    const opt = {
      margin: 10,
      filename: `${documentTitle.toLowerCase().replace(/\s+/g, '-')}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    // @ts-ignore
    html2pdf().set(opt).from(element).save();
  };

  const handleOpenBlockSelector = () => {
    // Открываем обычный BlockSelector для кнопки "+"
    setBlockSelectorPosition({
      x: Math.max(100, window.innerWidth - 300),
      y: Math.max(100, window.innerHeight - 400)
    });
    setShowBlockSelector(true);
  };

  // Обработчик кнопки сохранения
  const handleSave = () => {
    // Вызываем колбэк onSave без дополнительных действий
    // Просто вызываем и забываем
    if (onSave) onSave();
  };

  // Добавляем обработчик изменения заголовка
  const handleTitleUpdate = (newTitle: string) => {
    // Сохраняем текущий заголовок для последующего сохранения
    console.log('Заголовок обновлен:', newTitle);
    
    // Если есть callback для обновления заголовка, вызываем его
    if (onUpdateTitle && typeof onUpdateTitle === 'function') {
      console.log('RichTextEditor: вызван onUpdateTitle с новым заголовком:', newTitle);
      onUpdateTitle(newTitle);
    } else {
      console.warn('RichTextEditor: onUpdateTitle не определен или не является функцией');
    }
  };



  // Добавляем обработчик кликов по внутренним ссылкам
  useEffect(() => {
    if (!editor || !isEditing) return;
    
    const handleClick = (event: MouseEvent) => {
      // Получаем ссылку, по которой кликнули
      const target = event.target as HTMLElement;
      const link = target.closest('a');
      
      if (!link) return;
      
      // Проверяем, является ли ссылка внутренней
      const isInternalLink = link.hasAttribute('data-internal-link');
      
      if (isInternalLink) {
        // Предотвращаем стандартную навигацию
        event.preventDefault();
        
        // Получаем ID документа из атрибута
        const documentId = link.getAttribute('data-document-id');
        
        if (documentId) {
          // Создаем URL для навигации
          const url = `/knowledge/${documentId}`;
          
          // Эмулируем поведение стандартной ссылки - открываем в новой вкладке при Ctrl/Cmd+клик
          if (event.ctrlKey || event.metaKey) {
            window.open(url, '_blank');
          } else {
            // Иначе используем навигацию внутри приложения
            window.location.href = url;
          }
        }
      }
    };
    
    // Добавляем обработчик к редактору
    editor.view.dom.addEventListener('click', handleClick);
    
    // Очищаем обработчик при размонтировании
    return () => {
      editor.view.dom.removeEventListener('click', handleClick);
    };
  }, [editor, isEditing]);

  // Добавляем класс для стилизации внутренних ссылок
  useEffect(() => {
    // Добавляем CSS для стилизации внутренних ссылок
    const style = document.createElement('style');
    style.textContent = `
      a[data-internal-link="true"] {
        color: #4e4ae7 !important; /* Синий цвет для внутренних ссылок */
      }
    `;
    document.head.appendChild(style);
    
    // Очищаем стили при размонтировании
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Добавим эффект для вызова onSave при необходимости
  useEffect(() => {
    // Если есть изменения и автосохранение включено и не редактируется канбан
    if (contentChanged && autoSave && !kanbanChanging && itemId && editor) {
      console.log('[Редактор] Обнаружены изменения, вызываем обработчик сохранения');
      
      // Используем debounce для предотвращения частых вызовов
      const timer = setTimeout(() => {
        // Получаем актуальное содержимое редактора перед сохранением
        const actualContent = editor.getHTML();
        
        // Важно: обновляем локальное состояние перед вызовом onSave
        setCurrentContent(actualContent);
        
        // Сбрасываем флаг изменений
        setContentChanged(false);
        
        // Вызываем onSave, если он предоставлен, без обновления всего интерфейса
        if (onSave) {
          console.log('[Редактор] Вызываем onSave с актуальным содержимым:', actualContent.substring(0, 50) + '...');
          
          // Сначала обновляем состояние родителя
          onChange(actualContent);
          
          // Затем вызываем сохранение, но не ждем ответа от сервера для продолжения работы
          // Это позволяет избежать перерисовки
          onSave();
        }
      }, 2000); // 2 секунды задержка
      
      return () => clearTimeout(timer);
    }
  }, [contentChanged, autoSave, kanbanChanging, itemId, onSave, editor, onChange]);

  // Обработчик наведения мыши на ссылки
  useEffect(() => {
    if (!editor || !editorContentRef.current) return;
    
    console.log('[RichTextEditor] Инициализация обработчика наведения на ссылки');
    
    // Переменная для хранения последнего URL, для которого было показано превью
    let lastHoveredUrl = '';
    
    // Таймер для задержки скрытия превью
    let hidePreviewTimer: NodeJS.Timeout | null = null;
    
    // Функция показа превью
    const showPreview = (link: HTMLAnchorElement) => {
      const url = link.href;
      const rect = link.getBoundingClientRect();
      
      console.log('[RichTextEditor] Показываем превью для:', url);
      
      // Позиционируем превью точно под ссылкой (fixed позиционирование относительно viewport)
      const position = {
        x: rect.left + rect.width / 2,
        y: rect.bottom + 10 // 10px ниже ссылки
      };
      
      // Сохраняем ссылку для возможного последующего удаления
      lastSelectedLinkRef.current = link;
      
      // Определяем, является ли ссылка внутренней
      const isKnowledgeLink = url.includes('/knowledge/');
      const isRelativeUrl = url.startsWith('/');
      const isLocalDomain = url.includes(window.location.hostname);
      const isInternalLink = (isRelativeUrl && isKnowledgeLink) || (isLocalDomain && isKnowledgeLink);
      
      // Показываем соответствующий компонент предпросмотра
      if (isInternalLink) {
        setInternalLinkPreview({
          url,
          position,
          visible: true
        });
        setLinkPreview(null); // Закрываем внешний превью, если он был открыт
      } else {
        setLinkPreview({
          url,
          position,
          visible: true
        });
        setInternalLinkPreview(null); // Закрываем внутренний превью, если он был открыт
      }
      
      // Запоминаем URL, для которого показали превью
      lastHoveredUrl = url;
    };
    
    // Упрощенный обработчик наведения
    const handleLinkHover = (event: Event) => {
      const mouseEvent = event as MouseEvent;
      const target = mouseEvent.target as HTMLElement;
      const link = target.closest('a');
      
      // Отменяем таймер скрытия, если курсор над ссылкой
      if (hidePreviewTimer) {
        clearTimeout(hidePreviewTimer);
        hidePreviewTimer = null;
      }
      
      // Если курсор не над ссылкой, запускаем отложенное скрытие
      if (!link) {
        // Не запускаем таймер, если уже запущен
        if (!hidePreviewTimer && linkPreview) {
          hidePreviewTimer = setTimeout(() => {
            // Проверяем, не перешел ли курсор обратно на ссылку за это время
            const previewElement = document.querySelector('.link-preview-container');
            if (previewElement && previewElement.matches(':hover')) {
              console.log('[RichTextEditor] Курсор над превью, не скрываем');
              return;
            }
            
            console.log('[RichTextEditor] Скрываем превью по таймеру');
            setLinkPreview(null);
          }, 1000); // Большая задержка для удобства пользователя
        }
        return;
      }
      
      const url = link.href;
      
      // Проверяем, что:
      // 1. URL отличается от последнего показанного или
      // 2. Превью в данный момент не отображается
      if (url !== lastHoveredUrl || !linkPreview || !linkPreview.visible) {
        // Отменяем предыдущий таймер показа
        if (linkPreviewTimer.current) {
          clearTimeout(linkPreviewTimer.current);
        }
        
        // Устанавливаем таймер для задержки показа превью
        linkPreviewTimer.current = setTimeout(() => {
          // Повторно проверяем, что курсор все еще над ссылкой
          if (link.matches(':hover')) {
            showPreview(link);
          }
        }, 300);
      }
    };
    
    // Обработчик для отслеживания ухода курсора со страницы
    const handleMouseLeave = () => {
      // Если курсор покидает страницу, скрываем превью
      if (linkPreview) {
        console.log('[RichTextEditor] Курсор покинул страницу, скрываем превью');
        setLinkPreview(null);
      }
      
      if (internalLinkPreview) {
        console.log('[RichTextEditor] Курсор покинул страницу, скрываем превью внутренней ссылки');
        setInternalLinkPreview(null);
      }
    };
    
    // Используем для страницы обработчики mouseleave
    document.addEventListener('mouseleave', handleMouseLeave);
    
    // Добавляем делегированный обработчик mousemove для всего редактора
    const editorEl = editorContentRef.current.querySelector('.ProseMirror');
    
    if (editorEl) {
      console.log('[RichTextEditor] Добавляем слушатель событий для редактора');
      editorEl.addEventListener('mousemove', handleLinkHover as EventListener);
      
      // Добавляем обработчик клика для закрытия превью при клике в любой области редактора
      editorEl.addEventListener('click', () => {
        if (linkPreview) {
          console.log('[RichTextEditor] Клик в редакторе, скрываем превью');
          setLinkPreview(null);
        }
        
        if (internalLinkPreview) {
          console.log('[RichTextEditor] Клик в редакторе, скрываем превью внутренней ссылки');
          setInternalLinkPreview(null);
        }
      });
    } else {
      console.warn('[RichTextEditor] Не найден элемент редактора для добавления слушателя');
    }
    
    // Обработчик для превью, чтобы оно не закрывалось при наведении
    const handlePreviewInteraction = () => {
      if (hidePreviewTimer) {
        clearTimeout(hidePreviewTimer);
        hidePreviewTimer = null;
      }
    };
    
    // Добавляем глобальный обработчик для превью
    const addPreviewEventListeners = () => {
      const previewEl = document.querySelector('.link-preview-container');
      if (previewEl) {
        previewEl.addEventListener('mouseenter', handlePreviewInteraction);
      }
    };
    
    // Отслеживаем появление превью
    if (linkPreview && linkPreview.visible) {
      // Добавляем обработчики с небольшой задержкой после рендеринга
      setTimeout(addPreviewEventListeners, 100);
    }
    
    return () => {
      console.log('[RichTextEditor] Удаление обработчиков наведения на ссылки');
      
      if (linkPreviewTimer.current) {
        clearTimeout(linkPreviewTimer.current);
      }
      
      if (hidePreviewTimer) {
        clearTimeout(hidePreviewTimer);
      }
      
      const editorEl = editorContentRef.current?.querySelector('.ProseMirror');
      if (editorEl) {
        editorEl.removeEventListener('mousemove', handleLinkHover as EventListener);
      }
      
      document.removeEventListener('mouseleave', handleMouseLeave);
      
      const previewEl = document.querySelector('.link-preview-container');
      if (previewEl) {
        previewEl.removeEventListener('mouseenter', handlePreviewInteraction);
      }
      
      const internalPreviewEl = document.querySelector('.internal-link-preview-container');
      if (internalPreviewEl) {
        internalPreviewEl.removeEventListener('mouseenter', handlePreviewInteraction);
      }
    };
  }, [editor, linkPreview, internalLinkPreview]);

  // Функция для удаления ссылки
  const handleRemoveLink = () => {
    if (!editor) {
      console.error('[RichTextEditor] Редактор не инициализирован');
      return;
    }
    
    console.log('[RichTextEditor] Удаление ссылки');
    
    try {
      // Если есть конкретная ссылка, которую нужно удалить (при просмотре превью)
      if (lastSelectedLinkRef.current) {
        const linkElement = lastSelectedLinkRef.current;
        const linkText = linkElement.textContent || '';
        const href = linkElement.getAttribute('href') || '';
        
        console.log('[RichTextEditor] Удаляем конкретную ссылку:', href);
        
        // Получаем координаты ссылки для нахождения её в редакторе
        const rect = linkElement.getBoundingClientRect();
        const editorRect = editor.view.dom.getBoundingClientRect();
        
        // Относительная позиция внутри редактора
        const relX = rect.left - editorRect.left + (rect.width / 2);
        const relY = rect.top - editorRect.top + (rect.height / 2);
        
        // Находим позицию в документе ProseMirror
        const pos = editor.view.posAtCoords({ left: rect.left + rect.width/2, top: rect.top + rect.height/2 });
        
        if (pos) {
          console.log('[RichTextEditor] Найдена позиция для удаления:', pos);
          
          // Ищем в дереве документа нашу ссылку по позиции
          const { doc } = editor.state;
          let linkStart: number | null = null;
          let linkEnd: number | null = null;
          
          // Проходим по тексту вокруг найденной позиции
          doc.nodesBetween(Math.max(0, pos.pos - 100), Math.min(doc.content.size, pos.pos + 100), (node, pos) => {
            if (linkStart !== null && linkEnd !== null) return false; // Уже нашли
            
            // Проверяем, есть ли у этого узла метка ссылки
            const mark = node.marks.find(m => m.type.name === 'link');
            if (mark) {
              const markAttrs = mark.attrs;
              const markHref = markAttrs.href;
              
              // Проверяем совпадение href
              if (markHref === href || markHref.includes(href) || href.includes(markHref)) {
                linkStart = pos;
                linkEnd = pos + node.nodeSize;
                return false; // Прекращаем обход
              }
            }
            return true; // Продолжаем обход
          });
          
          if (linkStart !== null && linkEnd !== null) {
            console.log('[RichTextEditor] Найдена ссылка в документе:', linkStart, linkEnd);
            
            // Запоминаем текст ссылки до удаления
            const textContent = editor.state.doc.textBetween(linkStart, linkEnd);
            
            // Устанавливаем выделение на эту ссылку
            editor.commands.setTextSelection({ from: linkStart, to: linkEnd });
            
            // Даем время на обновление состояния
            setTimeout(() => {
              // Удаляем ссылку из выделенного текста
              editor.commands.unsetLink();
              
              // Сбрасываем выделение, устанавливая курсор в конец бывшей ссылки
              const cursorPos = linkStart !== null ? linkStart + textContent.length : 0;
              editor.commands.setTextSelection(cursorPos);
              
              // Снимаем выделение текста, оставляя только курсор
              setTimeout(() => {
                // Просто установить пустое выделение
                editor.commands.blur();
                
                // Обновляем содержимое
                onChange(editor.getHTML());
              }, 10);
            }, 10);
          } else {
            // Запасной вариант - попробуем найти ссылку по DOM
            const editorElement = editor.view.dom;
            const allLinks = editorElement.querySelectorAll('a');
            
            // Ищем ссылку с похожим href
            allLinks.forEach((link) => {
              const currentHref = link.getAttribute('href') || '';
              
              // Если совпадает href или текст
              if ((currentHref === href || currentHref.includes(href) || href.includes(currentHref)) && 
                  (link.textContent === linkText)) {
                
                console.log('[RichTextEditor] Найдена ссылка в DOM:', currentHref);
                
                // Сохраняем длину текста для последующего позиционирования курсора
                const textLength = link.textContent?.length || 0;
                
                // Создаем выделение
                const selection = window.getSelection();
                if (selection) {
                  selection.removeAllRanges();
                  const range = document.createRange();
                  range.selectNodeContents(link);
                  selection.addRange(range);
                  
                  // Обновляем состояние редактора на основе выделения
                  editor.commands.focus();
                  
                  // Удаляем ссылку
                  editor.commands.unsetLink();
                  
                  // Снимаем выделение, оставляя только курсор
                  setTimeout(() => {
                    editor.commands.blur();
                    onChange(editor.getHTML());
                  }, 10);
                }
              }
            });
          }
        } else {
          console.log('[RichTextEditor] Не удалось найти позицию для ссылки');
        }
      } else {
        // Если нет конкретной ссылки, работаем с текущим выделением
        console.log('[RichTextEditor] Удаляем ссылку из текущего выделения');
        
        // Запоминаем текущую позицию курсора
        const { from } = editor.state.selection;
        
        // Убедимся, что выделена именно ссылка, расширив выделение до её границ
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        
        // Возвращаем курсор на исходную позицию и снимаем выделение
        setTimeout(() => {
          editor.commands.setTextSelection(from);
          editor.commands.blur();
          onChange(editor.getHTML());
        }, 10);
      }
    } catch (error) {
      console.error('[RichTextEditor] Ошибка при удалении ссылки:', error);
    } finally {
      // Очищаем ссылку и закрываем превью в любом случае
      lastSelectedLinkRef.current = null;
      setLinkPreview(null);
      setInternalLinkPreview(null);
    }
  };

  return (
    <div className="relative w-full flex flex-col h-full">
      <div className={withBackground ? "bg-white dark:bg-gray-800 rounded-xl p-8" : ""}>
        <div className="flex justify-between items-center mb-6">
          {title && (
            <div className="mb-8">
              <EditableTitle
                title={title}
                onUpdate={(newTitle) => {
                  // Добавляем проверку на наличие функции обратного вызова
                  if (onUpdateTitle && typeof onUpdateTitle === 'function') {
                    console.log('RichTextEditor: вызван onUpdateTitle с новым заголовком:', newTitle);
                    onUpdateTitle(newTitle);
                  } else {
                    console.warn('RichTextEditor: onUpdateTitle не определен или не является функцией');
                  }
                }}
                onInputChange={onTitleInputChange}
                className="text-2xl font-bold text-gray-900 dark:text-white"
              />
            </div>
          )}
        </div>

        <div className="relative">
          <div 
            className="prose dark:prose-invert max-w-none" 
            ref={editorContentRef}
          >
            <EditorContent editor={editor} />
          </div>
        </div>

        {/* Рендерим панель инструментов через портал */}
        {editor && selectionCoords && ReactDOM.createPortal(
          <div 
            ref={toolbarRef}
            style={{
              position: 'fixed',
              top: `${selectionCoords.y}px`,
              left: `${selectionCoords.x}px`,
              transform: 'translate(-50%, 0)', // -50% только по X, чтобы центрировать горизонтально
              zIndex: 9999, // Увеличиваем z-index для портала
              // Добавляем стили напрямую вместо класса floating-toolbar
              background: document.documentElement.classList.contains('dark') ? '#1f2937' : '#fff',
              borderRadius: '6px',
              padding: '2px',
              pointerEvents: 'all' as const,
              boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
              border: document.documentElement.classList.contains('dark') ? '1px solid #374151' : '1px solid #e5e7eb',
              opacity: 1
            }}
            onMouseEnter={() => console.log('[Toolbar] Mouse enter, coords:', selectionCoords)}
          >
            <Toolbar editor={editor} />
          </div>,
          document.body
        )}
        
        {/* Рендерим предпросмотры через порталы */}
        {linkPreview && linkPreview.visible && ReactDOM.createPortal(
          <LinkPreview
            url={linkPreview.url}
            position={linkPreview.position}
            onClose={() => {
              setLinkPreview(null);
            }}
            onRemoveLink={handleRemoveLink}
          />,
          document.body
        )}
        
        {internalLinkPreview && internalLinkPreview.visible && ReactDOM.createPortal(
          <InternalLinkPreview
            url={internalLinkPreview.url}
            position={internalLinkPreview.position}
            onClose={() => {
              setInternalLinkPreview(null);
            }}
            onRemoveLink={handleRemoveLink}
          />,
          document.body
        )}

        {editor && (
          <>
            <BlockSelector
              editor={editor}
              isOpen={showBlockSelector}
              onClose={() => setShowBlockSelector(false)}
              position={blockSelectorPosition}
            />
            
            <HtmlBlockSelector
              editor={editor}
              isOpen={showHtmlBlockSelector}
              onClose={() => setShowHtmlBlockSelector(false)}
              position={htmlBlockSelectorPosition}
            />
          </>
        )}
      </div>
    </div>
  );
}

